import { useState } from "react";
import { Download, Share, SquarePlus, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useInstallPrompt } from "@/hooks/useInstallPrompt";
import { promptInstall } from "@/lib/pwa";

const DISMISS_KEY = "gt-install-dismissed";

const Step = ({ n, children }) => (
  <li className="flex items-start gap-2.5">
    <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-brand-gold-soft text-[12px] font-bold text-brand-gold-deep">
      {n}
    </span>
    <span className="text-[14px] leading-6 text-brand-ink">{children}</span>
  </li>
);

export const IosInstallDialog = ({ open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-[400px] rounded-[18px]">
      <DialogHeader>
        <DialogTitle className="brand-title text-xl">Install on iPhone or iPad</DialogTitle>
      </DialogHeader>
      <ol className="mt-1 space-y-3.5">
        <Step n="1">
          Tap the <Share size={15} className="mx-0.5 inline align-text-bottom text-brand" />
          <b>Share</b> button in Safari's toolbar.
        </Step>
        <Step n="2">
          Scroll down and choose{" "}
          <b>
            Add to Home Screen{" "}
            <SquarePlus size={15} className="ml-0.5 inline align-text-bottom text-brand" />
          </b>
          .
        </Step>
        <Step n="3">
          Tap <b>Add</b> — Greentek Alliance appears on your home screen.
        </Step>
      </ol>
      <p className="mt-3 text-[12px] text-brand-muted">
        On iPhone/iPad this only works from the Safari browser.
      </p>
    </DialogContent>
  </Dialog>
);

/** Reusable "Install app" button (e.g. in the profile menu). Renders nothing
 *  when the app is already installed or install isn't available. */
export const InstallButton = ({ className = "" }) => {
  const { canInstall, needsIosInstructions } = useInstallPrompt();
  const [iosOpen, setIosOpen] = useState(false);
  if (!canInstall && !needsIosInstructions) return null;

  const onClick = () => (canInstall ? promptInstall() : setIosOpen(true));
  return (
    <>
      <Button
        variant="outline"
        onClick={onClick}
        data-testid="install-app-button"
        className={`h-10 rounded-[12px] ${className}`}
      >
        <Download size={16} className="mr-1.5" /> Install app
      </Button>
      <IosInstallDialog open={iosOpen} onOpenChange={setIosOpen} />
    </>
  );
};

/** Dismissible bottom banner shown until the user installs or dismisses it. */
export const InstallBanner = ({ aboveNav = false }) => {
  const { installed, canInstall, needsIosInstructions } = useInstallPrompt();
  const [dismissed, setDismissed] = useState(
    () => typeof localStorage !== "undefined" && localStorage.getItem(DISMISS_KEY) === "1",
  );
  const [iosOpen, setIosOpen] = useState(false);

  if (installed || dismissed || (!canInstall && !needsIosInstructions)) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore private-mode storage errors */
    }
    setDismissed(true);
  };

  const onInstall = async () => {
    if (canInstall) {
      const outcome = await promptInstall();
      if (outcome === "accepted") setDismissed(true);
    } else {
      setIosOpen(true);
    }
  };

  return (
    <>
      <div
        data-testid="install-banner"
        className={`fixed inset-x-3 ${aboveNav ? "bottom-[calc(5.5rem+env(safe-area-inset-bottom))]" : "bottom-[calc(0.75rem+env(safe-area-inset-bottom))]"} z-50 mx-auto max-w-[440px] lg:inset-x-auto lg:bottom-6 lg:right-5 lg:w-[380px]`}
      >
        <div className="flex items-center gap-3.5 rounded-[18px] border border-black/[0.06] bg-white p-3.5 shadow-[0_16px_44px_-12px_rgba(8,36,33,0.30)]">
          <div className="grid h-11 w-11 shrink-0 place-items-center rounded-[13px] bg-brand-canvas">
            <img src="/logo-mark.png" alt="" className="h-8 w-8" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[14px] font-semibold text-brand-ink">Install the app</p>
            <p className="mt-0.5 text-[12px] leading-snug text-brand-muted">
              Add Greentek Alliance to your home screen.
            </p>
          </div>
          <Button
            size="sm"
            onClick={onInstall}
            className="h-9 shrink-0 rounded-[11px] bg-brand-forest px-4 text-[13px] font-semibold text-white hover:bg-brand-forest/90"
          >
            Install
          </Button>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss install prompt"
            className="-mr-0.5 shrink-0 rounded-lg p-1 text-brand-subtle transition hover:bg-black/[0.04] hover:text-brand-ink"
          >
            <X size={17} />
          </button>
        </div>
      </div>
      <IosInstallDialog open={iosOpen} onOpenChange={setIosOpen} />
    </>
  );
};
